// ============================================================
// CaseContextMenu.tsx
// Reusable positioned menu for case-list actions. Opened by
// right-click (at the cursor) or the row's ⋮ button (anchored to it).
// Adapts SINGLE vs MULTI based on how many cases are selected.
//
// Stage 1 wires the actions that already exist (open, client details,
// export PDF/CSV, copy reference, delete, clear selection). The NEW
// actions (Assign / Change status / Duplicate / Link / Open-in-new-tab)
// render as DISABLED "soon" placeholders so the structure is complete.
//
// The parent owns all behaviour — this component just renders items and
// calls the handler props. It closes on click-outside, Escape, and after
// any enabled action fires.
// ============================================================
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { MENU_SURFACE, MenuHeader, MenuIcon, MenuItem, MenuSep } from './menuUi'

export interface CaseContextMenuHandlers {
  onOpen: () => void
  onOpenNewTab: () => void
  onOpenClientDetails: () => void
  onExportPdf: () => void
  onExportCsv: () => void
  onCopyReference: () => void
  onDelete: () => void
  onAssign: () => void
  onDuplicate: () => void
  onRename: () => void
  onLink: () => void
  // Favourite / Unfavourite the single target (reuses the store's toggleFavourite
  // → the same cache the sidebar Favourites + the row star read, so all stay in
  // sync). Single-mode only.
  onToggleFavourite: () => void
  // Multi-select
  onClearSelection: () => void
}

interface CaseContextMenuProps {
  // Screen position (viewport coords) of the menu's top-left.
  x: number
  y: number
  // Number of selected cases; drives single vs multi layout. When the menu
  // was opened on a row NOT in the selection, the parent passes 1 (acts on
  // that single row).
  selectedCount: number
  // True when "Open client details" can resolve (the case has a client id).
  canOpenClient: boolean
  // Context-aware status transitions computed by the parent from the target
  // case's derived status (single) or the selection's statuses (multi):
  // e.g. "Finalise setup" (Draft→Open), "Close case…" (Open→Closed),
  // "Reopen" (Closed→Open). Rendered in the status slot.
  statusItems?: { label: string; onClick: () => void }[]
  // Whether the single target case is currently favourited — drives the
  // Favourite vs Unfavourite label/icon. Ignored in multi mode.
  isFavourited?: boolean
  // (Batch 4) false → hide Delete (the viewer isn't the assignee, an admin or
  // granted "Delete cases"). Omitted → shown, as before (the server decides).
  canDelete?: boolean
  // Display-only context header (mockup CA6261 .m-head): the single target's
  // name + reference. Omitted → no header. Ignored in multi mode (which shows
  // the "N cases" count instead).
  target?: { title: string; reference: string }
  handlers: CaseContextMenuHandlers
  onClose: () => void
}

const CaseContextMenu: React.FC<CaseContextMenuProps> = ({
  x, y, selectedCount, canOpenClient, statusItems, isFavourited, canDelete = true, target, handlers, onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  // ── Viewport clamp / flip ────────────────────────────────────────────
  // The menu is position:fixed at the cursor (top-left = x,y) and extends
  // down + right, so near the bottom/right edge the lower items overflow
  // off-screen and become unreachable. We MEASURE the rendered menu (its
  // height varies by item count — single vs multi + variable statusItems —
  // so a fixed size won't do) and, if it would overflow, FLIP it above the
  // cursor / shift it left, then CLAMP into the viewport with an 8px margin.
  //
  // useLayoutEffect (not useEffect) so the reposition happens AFTER the DOM
  // is measured but BEFORE the browser paints — the user never sees the
  // unclamped position flash. `pos` seeds at the raw cursor coords so there's
  // a valid first-paint position; the layout effect corrects it in the same
  // frame. When there's room, flip/clamp are no-ops and it opens at the cursor
  // exactly as before. Shared component → both the case-list right-click menu
  // and the sidebar case menu (via CaseActionsMenu) get this for free.
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: y, left: x })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const MARGIN = 8
    const { width, height } = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    // Horizontal: if it would run off the right edge, open to the LEFT of the
    // cursor; then clamp so it's never past either edge.
    let left = x + width + MARGIN > vw ? x - width : x
    left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - width - MARGIN))
    // Vertical: if it would run off the bottom, FLIP it above the cursor
    // (natural for a context menu); then clamp.
    let top = y + height + MARGIN > vh ? y - height : y
    top = Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - height - MARGIN))
    setPos({ top, left })
    // Re-measure when the cursor coords change OR the item count changes the
    // menu's size (multi layout + number of contextual status items).
  }, [x, y, selectedCount, statusItems?.length])

  // Close on click-outside + Escape.
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    // mousedown (not click) so it fires before the trigger's own handlers.
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const multi = selectedCount > 1

  // Run an action then close.
  const run = (fn: () => void) => () => { fn(); onClose() }

  // Restyled to the shared menu language (menuUi.tsx = mockup CA6261):
  // surface card, icon + label rows, soft separators, danger Delete, disabled
  // rows that say why. Items, order, gating and handlers are unchanged.
  return (
    <div
      ref={ref}
      role="menu"
      style={{ top: pos.top, left: pos.left }}
      className={`fixed z-[200] ${MENU_SURFACE}`}
      // Prevent a right-click on the menu itself from opening the browser menu.
      onContextMenu={(e) => e.preventDefault()}
    >
      {multi ? (
        <>
          <MenuHeader icon="layers" title="Selection" count={`${selectedCount} cases`} />
          <MenuItem icon={<MenuIcon name="csv" />} label="Export as CSV" onClick={run(handlers.onExportCsv)}
            trailing={<>{selectedCount} · <MenuIcon name="download" size={13} /></>} />
          <MenuItem icon={<MenuIcon name="assign" />} label="Assign to…" onClick={run(handlers.onAssign)} />
          {(statusItems ?? []).map((it, i) => (
            <MenuItem key={i} tone="status" icon={<MenuIcon name="check" />} label={it.label} onClick={run(it.onClick)} />
          ))}
          <MenuSep />
          <MenuItem icon={<MenuIcon name="x" />} label="Clear selection" onClick={run(handlers.onClearSelection)} />
          {canDelete && (
            <MenuItem tone="danger" icon={<MenuIcon name="trash" />} label={`Delete ${selectedCount} cases…`} onClick={run(handlers.onDelete)} />
          )}
        </>
      ) : (
        <>
          {target && <MenuHeader icon="briefcase" title={target.title} reference={target.reference} />}
          {/* ── OPEN group — primary actions first ── */}
          <MenuItem icon={<MenuIcon name="arrow" />} label="Open" onClick={run(handlers.onOpen)} />
          <MenuItem icon={<MenuIcon name="tab" />} label="Open in new tab" onClick={run(handlers.onOpenNewTab)}
            trailing={<MenuIcon name="external" size={13} />} />
          <MenuItem icon={<MenuIcon name="user" />} label="Open client details"
            disabled={!canOpenClient} disabledReason="No client linked"
            onClick={run(handlers.onOpenClientDetails)} />
          <MenuSep />
          {/* ── ORGANISE / EDIT group ── */}
          <MenuItem
            icon={<MenuIcon name="star" filled={!!isFavourited} />}
            // Gold = the favourite star (design-language §3.3 — an allowed gold use).
            iconClassName={isFavourited ? 'text-casevo-gold' : undefined}
            label={isFavourited ? 'Unfavourite' : 'Favourite'}
            onClick={run(handlers.onToggleFavourite)}
          />
          <MenuItem icon={<MenuIcon name="edit" />} label="Rename case…" onClick={run(handlers.onRename)} />
          <MenuItem icon={<MenuIcon name="assign" />} label="Assign to…" onClick={run(handlers.onAssign)} />
          {(statusItems ?? []).map((it, i) => (
            <MenuItem key={i} tone="status" icon={<MenuIcon name="check" />} label={it.label} onClick={run(it.onClick)} />
          ))}
          <MenuItem icon={<MenuIcon name="link" />} label="Link to case…" onClick={run(handlers.onLink)} />
          <MenuSep />
          {/* ── DUPLICATE / EXPORT group ── */}
          <MenuItem icon={<MenuIcon name="layers" />} label="Duplicate case…" onClick={run(handlers.onDuplicate)} />
          <MenuItem icon={<MenuIcon name="pdf" />} label="Export as PDF…" onClick={run(handlers.onExportPdf)} />
          {/* Opens the Export case dialog on the CSV view (preview + filename). */}
          <MenuItem icon={<MenuIcon name="csv" />} label="Export as CSV…" onClick={run(handlers.onExportCsv)} />
          <MenuItem icon={<MenuIcon name="copy" />} label="Copy reference" onClick={run(handlers.onCopyReference)} />
          {canDelete && (
            <>
              <MenuSep />
              {/* ── DESTRUCTIVE — always last, separated ── */}
              <MenuItem tone="danger" icon={<MenuIcon name="trash" />} label="Delete case…" onClick={run(handlers.onDelete)} />
            </>
          )}
        </>
      )}
    </div>
  )
}

export default CaseContextMenu
